/**
 * Component used for checking if requested serviceType is available in specific serviceLocation.
 * In case serviceType is not supported user should be redirected and see relevant toast.
 */
import React from "react";
import { useHistory } from "react-router";
import { useSelector } from "react-redux";

import { ROUTES } from "@config/app.routing";

import { SERVICE_TYPE } from "@typings/globals";
import { LOADING_STATE } from "@typings/reduxThunkActions";

import { showToast } from "@containers/Toastify";

import { useTranslate } from "@hooks/useTranslate";
import { useAuth } from "@hooks/useAuth";
import { useLocalisation } from "@hooks/useLocalisation";

import {
  getServiceLocationInfo,
  getServiceLocationLoadingState,
} from "@app/redux/serviceLocation/serviceLocation.selectors";

interface Props {
  serviceType: SERVICE_TYPE;
  postalCodeQuery: string;
}

const ServiceTypeAvailabilityChecker = (props: Props): null => {
  const history = useHistory();
  const translate = useTranslate();
  const { isUserLoggedIn, isUserDataFetched } = useAuth();
  const { language } = useLocalisation();

  const serviceLocationInfo = useSelector(getServiceLocationInfo);
  const serviceLocationLoadingState = useSelector(
    getServiceLocationLoadingState,
  );

  const serviceLocationZip = serviceLocationInfo.get("zip");
  const isServiceTypeSupported = serviceLocationInfo
    .get("serviceTypes")
    .includes(props.serviceType);
  const isServiceLocationFetched =
    serviceLocationLoadingState !== LOADING_STATE.IDLE &&
    serviceLocationLoadingState !== LOADING_STATE.LOADING;

  React.useEffect(() => {
    /**
     * Prevent any action before data is loaded.
     */
    if (!isServiceLocationFetched) return;
    if (!isUserDataFetched) return;

    /**
     * Requested postal code was found and is covered by our cleaning services.
     * Do nothing.
     */
    if (isServiceTypeSupported && serviceLocationZip) return;

    /**
     * Redirect logged in user to My Cleanings and show relevant toast.
     */
    if (isUserLoggedIn) {
      history.push(
        `${ROUTES.CLIENT_DASHBOARD}${ROUTES.CLIENT_DASHBOARD_MY_CLEANINGS}`,
      );
      showToast(
        translate("batmaid_pwa_generic.uncovered_code_subscription_title"),
        { theme: "dark" },
      );
    } else {
      /**
       * Redirect anonymous user to Landing Page and show `UncoveredCodeSubscriptionModal`.
       */
      window.location.href = `/${language}/?uncoveredZip=${props.postalCodeQuery}&uncoveredServiceType=${props.serviceType}`;
    }
  }, [
    props.serviceType,
    props.postalCodeQuery,
    isUserDataFetched,
    isServiceLocationFetched,
    isServiceTypeSupported,
    serviceLocationZip,
    isUserLoggedIn,
    history,
    translate,
    language,
  ]);

  return null;
};

export { ServiceTypeAvailabilityChecker };
